import { Controller } from '@hotwired/stimulus'

export default class SellingOfferFormController extends Controller {
  static targets = [
    'priceInput',
    'warningDiscount',
    'higherPriceWarning',
    'capitalGainPercent',
    'warningCap',
    'priceHelper',
    'invalidFeedback',
    'extremeLossWarning',
    'extremeLossCheckbox',
    'extremeLossCheckboxContainer',
    'submitButton',
    'capitalLoss',
    'capitalLossPercent',
    'automaticWarningEqual',
    'automaticWarningBelow',
    'automaticWarningAbove',
    'automaticWarningPercent',
  ];

  static values = {
    crd: Number,
    interests: Number,
  };

  connect() {
    this._debounceTimer = null;
    this._currencyFormatter = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' });
    this._maxPrice = this.crdValue + this.interestsValue;
    this._update();
  }

  onPriceChange() {
    clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => this._update(), 300);
  }

  onExtremeLossCheckboxChange() {
    this._updateSubmitButton();
  }

  _update() {
    const price = parseFloat(this.priceInputTarget.value) || 0;
    const formattedPrice = this._currencyFormatter.format(price);
    const prefix = this.submitButtonTarget.dataset.sellingOfferFormSubmitPrefixValue;
    if (prefix) {
      this.submitButtonTarget.innerText = `${prefix} ${formattedPrice}`;
    }

    this._hideAllWarnings();

    if (price < 1) {
      this.priceInputTarget.classList.add('is-invalid');
      this.invalidFeedbackTarget.classList.add('d-block');
      this.priceHelperTarget.classList.add('d-none');
      this.extremeLossCheckboxContainerTarget.classList.add('d-none');
    } else if (price > this._maxPrice) {
      this.priceInputTarget.classList.add('is-invalid');
      this.priceHelperTarget.classList.add('d-none');
      this.warningCapTarget.classList.add('d-block');
    } else if (price > this.crdValue) {
      const percent = ((price - this.crdValue) / this.crdValue * 100).toFixed(0);
      this.capitalGainPercentTarget.textContent = `${percent}%`;
      this.higherPriceWarningTarget.classList.remove('d-none');
    } else if (price < this.crdValue) {
      this.priceInputTarget.classList.remove('is-invalid');
      const loss = this.crdValue - price;
      const percent = ((this.crdValue - price) / this.crdValue * 100).toFixed(0);
      const extremeLoss = price < this.crdValue * 0.85;
      this.capitalLossTargets.forEach(el => el.textContent = this._currencyFormatter.format(loss));
      this.capitalLossPercentTargets.forEach(el => el.textContent = `${percent}%`);
      extremeLoss ? this.extremeLossWarningTarget.classList.toggle('d-none') : this.warningDiscountTarget.classList.remove('d-none');
    }

    this._updateAutomaticWarning(price);
    this._updateSubmitButton();
  }

  _updateAutomaticWarning(price) {
    this.automaticWarningEqualTarget.classList.add('d-none');
    this.automaticWarningBelowTarget.classList.add('d-none');
    this.automaticWarningAboveTarget.classList.add('d-none');

    const percentFormatter = new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

    if (price < 1 || price === this.crdValue) {
      this.automaticWarningEqualTarget.classList.remove('d-none');
    } else if (price < this.crdValue) {
      const percent = percentFormatter.format((this.crdValue - price) / this.crdValue * 100);
      this.automaticWarningPercentTargets.forEach(el => el.textContent = percent);
      this.automaticWarningBelowTarget.classList.remove('d-none');
    } else {
      const percent = percentFormatter.format((price - this.crdValue) / this.crdValue * 100);
      this.automaticWarningPercentTargets.forEach(el => el.textContent = percent);
      this.automaticWarningAboveTarget.classList.remove('d-none');
    }
  }

  _hideAllWarnings() {
    this.warningDiscountTarget.classList.add('d-none');
    this.extremeLossWarningTarget.classList.add('d-none');
    this.higherPriceWarningTarget.classList.add('d-none');
    this.warningCapTarget.classList.remove('d-block');
    this.invalidFeedbackTarget.classList.remove('d-block');
    this.priceInputTarget.classList.remove('is-invalid');
    this.extremeLossCheckboxContainerTarget.classList.add('d-none');
    this.extremeLossCheckboxTarget.required = false;
  }

  _updateSubmitButton() {
    const price = parseFloat(this.priceInputTarget.value) || 0;

    if (price < 1) {
      this.submitButtonTarget.disabled = true;
    } else if (price > this._maxPrice) {
      this.submitButtonTarget.disabled = true;
    } else if (price < this.crdValue * 0.85 && price >= 1) {
      this.extremeLossCheckboxContainerTarget.classList.remove('d-none');
      this.extremeLossCheckboxTarget.required = true;
      this.submitButtonTarget.disabled = !this.extremeLossCheckboxTarget.checked;
    } else {
      this.submitButtonTarget.disabled = false;
    }
  }
}
